/** @jsx jsx */
import { jsx, Link, Label } from '@front10/base-elements';
import { useSelect } from 'downshift';
import * as React from 'react';
import { FilterButton } from './FilterButton';
import { Input } from './Input';
import Popover, { positionDefault, positionRight } from 'popover-reach-fr';
import { useRect } from '@reach/rect';
import { useIsMobileView, useTranslation } from '@front10/utils';
import nanoid from 'nanoid';
import { isSSR } from '@front10/utils';
import VisuallyHidden from '@reach/visually-hidden';
import { Context } from '@front10/helpers/dist/Context';

interface Props {
  label: string;
  prefixSelectedItem?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  ariaLabelDescription?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: string;
  options: Option[];
  name?: string;
  portal?: boolean;
  onChange?: (value: string) => void;
  onClick?: () => void;
  showLabel?: boolean;
  layout?: 'compact' | 'standard' | 'closed';
  labelNotSelectedItem?: string;
  forcePlaceholder?: boolean;
  loading?: boolean;
  focusWhenIsShow?: boolean;
  filterButtonIconProps?: $TSFixMe;
  filterButtonIconLeftProps?: $TSFixMe;
  activeFilterButton?: boolean;
  renderItem?: (
    item: $TSFixMe,
    itemsProps: $TSFixMe,
    itemSelected: $TSFixMe,
    itemHighlighted: $TSFixMe
  ) => void;
  dataTestIdButton?: string;
  dataTestIdPopover?: string;
  className?: string;
  filterContentRef?: React.RefObject<HTMLDivElement>;
  hasError?: boolean;
}

interface Option {
  value: string;
  label: React.ReactNode;
  href?: string;
  testId?: string;
}

const styles = {
  menu: {
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    listStyle: 'none',
    position: 'relative'
  }
} as const;

const ListboxCmp = (props: Props, ref: any) => {
  const TAB_KEY = 9;
  const ESC_KEY = 27;
  const items = props.options;
  const [id] = React.useState(nanoid());
  const [isOpen, setIsOpen] = React.useState(false);
  const translate = useTranslation();
  const layout = props.layout ?? 'compact';
  const showLabel = props.showLabel ?? layout !== 'compact';
  const defaultSelectedItem = React.useMemo(
    () => props.options.find((elem) => elem.value === props.defaultValue),
    [props.defaultValue, props.options]
  );
  const toggleRef = React.useRef<HTMLButtonElement | null>(null);
  const rect = useRect(toggleRef);

  const stateReducer = (state: any, actionAndTypeChanges: any) => {
    const { type, changes } = actionAndTypeChanges;
    setIsOpen(changes.isOpen);
    if (type === useSelect.stateChangeTypes.MenuMouseLeave) {
      return {
        ...changes,
        highlightedIndex: state.highlightedIndex
      };
    }
    return { ...changes };
  };

  const {
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    closeMenu
  } = useSelect({
    id: id,
    items,
    selectedItem: defaultSelectedItem,
    stateReducer,
    onStateChange: () => props.onClick?.(),
    onSelectedItemChange: (event) => {
      props.onChange?.(event.selectedItem?.value || '');
      const element: any = document?.querySelector(`#listBoxId-${id}`);
      if (element) {
        element.focus();
      }
    },
    itemToString: (item) => (item ? String(item.label) : '')
  });
  const { direction } = React.useContext(Context);

  React.useEffect(() => {
    const newFocusWhenIsShow = props.focusWhenIsShow === undefined ? true : props.focusWhenIsShow;
    const ac = new AbortController();
    if (!isOpen && !props.loading) {
      if (newFocusWhenIsShow) {
        toggleRef.current?.focus();
      }
    }
    return () => {
      // Abort both fetches on unmount
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, isOpen, props.loading]);

  const improveIdToggleButtonProps = (data: any) => {
    const baseId = data.id;
    return {
      ...data,
      id: `${baseId}-${id}`,
      'aria-labelledby': data['aria-labelledby'].replace(baseId, `${baseId}-${id}`),
      className: props?.className
    };
  };
  const improveIdlabelProps = (data: any) => {
    const baseId = data.id;
    return {
      ...data,
      id: `${baseId}-${id}`
    };
  };
  const improveId = (data: any, baseId: any, newId: any) => {
    return {
      ...data,
      'aria-labelledby': data['aria-labelledby'].replace(baseId, `${newId}`)
    };
  };

  const privateTargetRef: React.RefObject<HTMLElement> = React.useRef(null);
  const { portal = false } = props;
  const isMobile = useIsMobileView();

  let toggleButtonProps = improveIdToggleButtonProps(getToggleButtonProps({ ref: toggleRef }));
  let labelProps = getLabelProps();
  const oldId = labelProps.id;
  labelProps = improveIdlabelProps(labelProps);
  toggleButtonProps = improveId(toggleButtonProps, oldId, labelProps.id);
  let menuProps = improveId(
    getMenuProps({
      'aria-activedescendant': `${id}-item-${highlightedIndex}`,
      style: styles.menu,
      onBlur: () => {
        setIsOpen(false);
      }
    }),
    oldId,
    labelProps.id
  );
  menuProps['aria-labelledby'] = `listBoxId-${id}`;
  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        if (!isSSR()) {
          const element: any = document?.querySelector(`#listBoxId-${id}`);
          if (element) {
            element.focus();
          }
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    const closeOnScroll = () => {
      closeMenu();
    };

    const element = isMobile
      ? props.filterContentRef?.current?.parentElement
      : props.filterContentRef?.current;

    element?.addEventListener('scroll', closeOnScroll);

    return () => element?.removeEventListener('scroll', closeOnScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterContentRef, isMobile]);

  const dropdown = (
    <React.Fragment>
      <VisuallyHidden aria-live="polite">
        {isOpen ? translate('cmp-listbox-filter-expanded', 'Filter expanded') : ''}
      </VisuallyHidden>
      <FilterButton
        {...toggleButtonProps}
        iconLeftSettings={props?.filterButtonIconLeftProps}
        iconSettings={props?.filterButtonIconProps}
        active={
          props?.activeFilterButton !== undefined
            ? props?.activeFilterButton
            : !!selectedItem?.value && layout === 'compact'
        }
        value={
          (!props.forcePlaceholder &&
            `${
              props.prefixSelectedItem !== null && props.prefixSelectedItem !== undefined
                ? props.prefixSelectedItem + ' ' + selectedItem?.label
                : selectedItem?.label
                ? selectedItem?.label
                : props.label
            }`) ||
          props.placeholder ||
          props.labelNotSelectedItem ||
          translate('cmp-listbox-not-selected-item', 'Elements')
        }
        layout={layout}
        aria-labelledby={
          props.ariaLabelledBy !== undefined
            ? `${toggleButtonProps['aria-labelledby']} ${props.ariaLabelledBy}`
            : showLabel
            ? toggleButtonProps['aria-labelledby']
            : null
        }
        aria-describedby={
          props.ariaDescribedBy !== undefined
            ? `${toggleButtonProps['aria-describedby'] ?? ''} ${props.ariaDescribedBy}`
            : toggleButtonProps['aria-describedby']
        }
        aria-label={`${
          selectedItem?.label
            ? `${translate('cmp-travel-class-filter-label-selected-item', 'Select')} ${
                props.label
              } ${selectedItem?.label}`
            : showLabel
            ? undefined
            : props.label
        } `}
        disabled={props.disabled}
        id={`listBoxId-${id}`}
        aria-haspopup="listbox"
        aria-controls={isOpen ? menuProps.id : null}
        data-testid={props.dataTestIdButton}
        aria-invalid={props.hasError}
      />
      <Popover
        targetRef={portal ? toggleRef : privateTargetRef}
        portal={portal}
        style={{ minWidth: rect?.width }}
        sx={{ variant: isOpen ? 'layout.listbox' : '' }}
        onKeyDown={(e) => {
          if (e.keyCode === TAB_KEY || e.keyCode === ESC_KEY) {
            e.preventDefault();
            if (!isSSR()) {
              const element: any = document?.querySelector(`#listBoxId-${id}`);
              if (element) {
                element.focus();
              }
            }
          }
        }}
        position={direction === 'ltr' ? positionDefault : positionRight}
        data-testid={props.dataTestIdPopover}
      >
        {isOpen && (
          <ul {...menuProps} sx={{ variant: 'menus.default' }} role={'listbox'}>
            {items.map((item, index) => {
              const itemSelected = selectedItem && selectedItem.value === items[index].value;
              const itemHighlighted = highlightedIndex === index;
              let ariaLabel = `${item.label}`;
              if (itemSelected) {
                ariaLabel = `${translate('cmp-listbox-item-selected', 'Selected')} ${ariaLabel}`;
              }
              let itemProps = getItemProps({ item, index, 'aria-selected': itemSelected ?? false });
              itemProps['aria-label'] = ariaLabel;
              if (typeof props.renderItem === 'function') {
                return props.renderItem(item, { ...itemProps }, itemSelected, itemHighlighted);
              } else {
                const optionElement = (
                  <li
                    data-item-selected={itemSelected}
                    data-item-highlighted={itemHighlighted}
                    sx={{
                      variant: 'menuItems.default',
                      cursor: item.href ? 'cursor' : 'default'
                    }}
                    key={`${item.value}-list`}
                    {...itemProps}
                    data-testid={item.testId ? item.testId : null}
                  >
                    {item.label}
                  </li>
                );

                if (item.href) {
                  return (
                    <li
                      data-item-selected={itemSelected}
                      data-item-highlighted={itemHighlighted}
                      sx={{
                        cursor: item.href ? 'cursor' : 'default'
                      }}
                      key={`${item.value}-list`}
                      {...getItemProps({ item, index })}
                    >
                      <Link key={item.value} href={item.href}>
                        {optionElement}
                      </Link>
                    </li>
                  );
                }
                return optionElement;
              }
            })}
          </ul>
        )}
      </Popover>
    </React.Fragment>
  );

  if (layout === 'closed') {
    return (
      <Input hasError={props.hasError} label={props.label}>
        <div sx={{ position: 'relative', variant: 'layout.closed' }}>{dropdown}</div>
      </Input>
    );
  }

  const renderLabel = showLabel ? (
    <Label
      variant="text.fontSize6"
      color="text"
      mb="2"
      {...{ ...labelProps, htmlFor: `${toggleButtonProps.id}` }}
    >
      {props.label}
    </Label>
  ) : null;

  return (
    <div sx={{ position: 'relative' }}>
      {renderLabel}
      {dropdown}
    </div>
  );
};

const Listbox = React.forwardRef(ListboxCmp);

export { Listbox };
